import React, { memo, useEffect, useRef, useState } from 'react';
import { renderSampleFrames, thumbnailCount } from '../../lib/creatorThumbnails';

const identityUrl = value => value;
const imageStyle = { display: 'block', width: '100%', height: '100%', minWidth: 0, objectFit: 'contain', flex: '1 1 0', background: '#000000' };

function CreatorVideoThumbnails({ asset, clip, sessionUrl = identityUrl, pixelWidth }) {
  const container = useRef(null);
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === 'undefined');
  const [result, setResult] = useState(null);
  const count = thumbnailCount(pixelWidth);
  const url = asset?.kind === 'video' ? sessionUrl(asset.proxy_url || asset.url) : '';
  const fallback = asset?.thumbnail_url ? sessionUrl(asset.thumbnail_url) : '';
  const sourceStart = clip?.source_start ?? 0, duration = clip?.duration, speed = clip?.speed ?? 1;
  const identity = JSON.stringify([url, sourceStart, duration, speed, count]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const node = container.current;
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.target === node) setVisible(entry.isIntersecting);
    }, { root: node.closest('[data-testid="creator-timeline"]'), rootMargin: '90px', threshold: 0 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !url) return undefined;
    const controller = new AbortController();
    renderSampleFrames({ url, clip: { source_start: sourceStart, duration, speed }, pixelWidth: count * 90, signal: controller.signal })
      .then(frames => { if (!controller.signal.aborted) setResult({ identity, frames }); })
      .catch(() => { /* The existing thumbnail remains available on timeout, cancellation or decode failure. */ });
    return () => controller.abort();
  }, [visible, url, sourceStart, duration, speed, count, identity]);

  const frames = result?.identity === identity ? result.frames : [];
  return <span ref={container} className="creator-video-thumbnails" aria-hidden="true"
    style={{ position: 'absolute', inset: 0, display: 'flex', overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
    {frames.length ? frames.map((frame, index) => <img key={`${frame.time}-${index}`} className="creator-video-thumbnail" src={frame.src}
      alt="" draggable={false} decoding="async" data-sample-time={frame.time} style={imageStyle} />)
      : fallback && <img className="creator-video-thumbnail-fallback" src={fallback} alt="" draggable={false}
        loading="lazy" decoding="async" referrerPolicy="no-referrer" style={imageStyle} />}
  </span>;
}

export default memo(CreatorVideoThumbnails);
